import {
  CircleGeometry,
  Color,
  CubicBezierCurve3,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
  Vector3,
} from 'three';
import {Line2} from 'three/examples/jsm/lines/Line2.js';
import {LineGeometry} from 'three/examples/jsm/lines/LineGeometry.js';
import {LineMaterial} from 'three/examples/jsm/lines/LineMaterial.js';

import {RgbaU32} from './types/colors';

const COLOR_OPERATIONS = `
      vec4 decodeColor(uint v) {
        return vec4(
            float((v & 0xff000000u) >> 24u) / 255.,
            float((v & 0x00ff0000u) >> 16u) / 255.,
            float((v & 0x0000ff00u) >>  8u) / 255.,
            float((v & 0x000000ffu) >>  0u) / 255.);
      }
`;
const SHADOW_900_RGBA = 0x04040466 as RgbaU32;

// TODO(april): import this from somewhere
const TYPE_IGNORE = 'ignore';

export enum Corner {
  BOTTOM_LEFT = 1,
  BOTTOM_RIGHT = 2,
  TOP_LEFT = 4,
  TOP_RIGHT = 8,
  ALL = 15,
}

export class Circle extends Mesh {
  constructor({
    fill,
    name,
    radius,
    stroke,
    visible,
    z,
  }: {
    fill: number;
    name?: string;
    radius: number;
    stroke: number;
    visible?: boolean;
    z: number;
  }) {
    super(
      new CircleGeometry(radius, 24),
      Circle.makeMaterial({fill, radius, stroke}),
    );
    if (name) {
      this.name = name;
    }
    this.geometry.computeBoundingBox();
    this.position.z = z;
    this.visible = visible ?? true;
  }

  get fill(): number {
    return (this.material as ShaderMaterial).uniforms.fill.value;
  }

  set fill(rgba: number) {
    (this.material as ShaderMaterial).uniforms.fill.value = rgba;
  }

  get stroke(): number {
    return (this.material as ShaderMaterial).uniforms.stroke.value;
  }

  set stroke(rgba: number) {
    (this.material as ShaderMaterial).uniforms.stroke.value = rgba;
  }

  private static makeMaterial({
    fill,
    radius,
    stroke,
  }: {
    fill: number;
    radius: number;
    stroke: number;
  }): ShaderMaterial {
    const material = new ShaderMaterial({
      vertexShader: `
        uniform float radius;

        out float fragRadius;

        void main() {
          fragRadius = gl_VertexID > 0 ? radius : 0.;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform uint fill;
        uniform float radius;
        uniform uint stroke;
        uniform uint round;

        in float fragRadius;

        ${COLOR_OPERATIONS}

        void main() {
          float a = fragRadius < radius - 1. ? 0. : 1.;
          gl_FragColor = (1. - a) * decodeColor(fill) + a * decodeColor(stroke);
        }
      `,
      uniforms: {
        stroke: {value: stroke},
        fill: {value: fill},
        radius: {value: radius},
      },
    });
    material.transparent = true;
    return material;
  }
}

export class DropShadow extends Mesh {
  constructor({
    fill,
    size,
    radius,
    z,
  }: {
    fill?: RgbaU32;
    size: Vector2;
    radius: number;
    z: number;
  }) {
    super(
      new PlaneGeometry(size.x + 12, size.y + 12),
      DropShadow.makeMaterial({
        fill: fill ?? SHADOW_900_RGBA,
        size,
        radius,
      }),
    );
    this.name = TYPE_IGNORE;
    this.position.z = z;
  }

  get fill(): RgbaU32 | undefined {
    return (this.material as ShaderMaterial).uniforms.fill.value;
  }

  set fill(rgba: RgbaU32 | undefined) {
    (this.material as ShaderMaterial).uniforms.fill.value =
      rgba ?? SHADOW_900_RGBA;
  }

  get size(): Vector2 {
    return (this.material as ShaderMaterial).uniforms.size.value;
  }

  set size(size: Vector2) {
    this.geometry.dispose();
    this.geometry = new PlaneGeometry(size.x + 12, size.y + 12);
    (this.material as ShaderMaterial).uniforms.size.value = new Vector2(
      size.x + 12,
      size.y + 12,
    );
  }

  private static makeMaterial({
    fill,
    size,
    radius,
  }: {
    fill: RgbaU32;
    size: Vector2;
    radius: number;
  }): ShaderMaterial {
    const material = new ShaderMaterial({
      vertexShader: `
        uniform float radius;

        out vec2 fragPosition;

        void main() {
          fragPosition = position.xy;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform uint fill;
        uniform vec2 size;
        uniform float radius;

        in vec2 fragPosition;

        ${COLOR_OPERATIONS}

        float rounded(vec2 d) {
          float horizontal =
              (1. - clamp(-abs(d.x), 0., 1.))
                  * (1. - clamp(radius - abs(d.y), 0., 1.));
          float vertical =
              (1. - clamp(radius - abs(d.x), 0., 1.))
                  * (1. - clamp(-abs(d.y), 0., 1.));
          float rounding = 1. - clamp(length(d + radius) - radius, 0., 1.);
          return max(max(horizontal, vertical), rounding);
        }

        void main() {
          vec2 outside = abs(fragPosition) - size / 2.;
          vec2 offset = vec2(outside.x, fragPosition.y > 0. ? outside.y + radius : outside.y);
          bool crop = fragPosition.y + radius > size.y / 2.;
          float r = rounded(offset);
          vec2 a = 1. - clamp((radius - abs(offset)) / radius, 0., 1.);
          vec4 shadow = a.x * a.y * decodeColor(fill);

          gl_FragColor = (crop ? 0. : 1.) * r * r * shadow;
        }
      `,
      uniforms: {
        fill: {value: fill},
        size: {value: new Vector2(size.x + 12, size.y + 12)},
        radius: {value: radius},
      },
    });
    material.transparent = true;
    return material;
  }
}

export class RoundedRect extends Mesh {
  constructor({
    fill,
    name,
    radius,
    round,
    size,
    stroke,
    visible,
    z,
  }: {
    fill: number;
    name?: string;
    radius: number;
    round?: number;
    size: Vector2;
    stroke: number;
    visible?: boolean;
    z: number;
  }) {
    super(
      new PlaneGeometry(size.x, size.y),
      RoundedRect.makeMaterial({fill, radius, round, size, stroke}),
    );
    if (name) {
      this.name = name;
    }
    this.geometry.computeBoundingBox();
    this.position.z = z;
    this.visible = visible ?? true;
  }

  get fill(): number {
    return (this.material as ShaderMaterial).uniforms.fill.value;
  }

  set fill(rgba: number) {
    (this.material as ShaderMaterial).uniforms.fill.value = rgba;
  }

  get size(): Vector2 {
    return (this.material as ShaderMaterial).uniforms.size.value;
  }

  set size(size: Vector2) {
    this.geometry.dispose();
    this.geometry = new PlaneGeometry(size.x, size.y);
    this.geometry.computeBoundingBox();
    (this.material as ShaderMaterial).uniforms.size.value = size;
  }

  get stroke(): number {
    return (this.material as ShaderMaterial).uniforms.stroke.value;
  }

  set stroke(rgba: number) {
    (this.material as ShaderMaterial).uniforms.stroke.value = rgba;
  }

  private static makeMaterial({
    fill,
    radius,
    round,
    size,
    stroke,
  }: {
    fill: number;
    radius: number;
    round?: number;
    size: Vector2;
    stroke: number;
  }): ShaderMaterial {
    const material = new ShaderMaterial({
      vertexShader: `
        uniform vec2 size;

        out vec2 fragPosition;

        void main() {
          fragPosition = position.xy;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec2 size;
        uniform uint fill;
        uniform float radius;
        uniform uint stroke;
        uniform uint round;

        in vec2 fragPosition;

        ${COLOR_OPERATIONS}

        float rounded(vec2 position, vec2 d, float take) {
          float horizontal =
              (1. - clamp(take - abs(d.x), 0., 1.))
                  * (1. - clamp(take + radius - abs(d.y), 0., 1.));
          float vertical =
              (1. - clamp(take + radius - abs(d.x), 0., 1.))
                  * (1. - clamp(take - abs(d.y), 0., 1.));
          float bottomLeft =
              (round & 1u) != 0u || position.y > 0. || position.x > 0.
                  ? 0.
                  :
                    (1. - clamp(take - abs(d.x), 0., 1.))
                        * (1. - clamp(take - abs(d.y), 0., 1.));
          float bottomRight =
              (round & 2u) != 0u || position.y > 0. || position.x < 0.
                  ? 0.
                  :
                    (1. - clamp(take - abs(d.x), 0., 1.))
                        * (1. - clamp(take - abs(d.y), 0., 1.));
          float topLeft =
              (round & 4u) != 0u || position.y < 0. || position.x > 0.
                  ? 0.
                  :
                    (1. - clamp(take - abs(d.x), 0., 1.))
                        * (1. - clamp(take - abs(d.y), 0., 1.));
          float topRight =
              (round & 8u) != 0u || position.y < 0. || position.x < 0.
                  ? 0.
                  :
                    (1. - clamp(take - abs(d.x), 0., 1.))
                        * (1. - clamp(take - abs(d.y), 0., 1.));
          float cornered = max(max(max(bottomLeft, bottomRight), topLeft), topRight);
          float rounding = 1. - clamp(length(d + radius) - radius + take, 0., 1.);
          return max(max(max(horizontal, vertical), cornered), rounding);
        }

        void main() {
          vec2 outside = abs(fragPosition) - size / 2.;
          float filled = rounded(fragPosition, outside, 1.5);
          float stroked = rounded(fragPosition, outside, 0.) * (1. - filled);
          gl_FragColor = filled * decodeColor(fill) + stroked * decodeColor(stroke);
        }
      `,
      uniforms: {
        size: {value: size},
        round: {value: round ?? Corner.ALL},
        stroke: {value: stroke},
        fill: {value: fill},
        radius: {value: radius},
      },
    });
    material.transparent = true;
    return material;
  }
}

const SPLINE_POINT_COUNT = 24;

export class Spline extends Line2 {
  private curve: number[];
  private _fillStart: RgbaU32;
  private _fillEnd: RgbaU32;

  constructor({
    fillStart,
    fillEnd,
    lineWidth,
    name,
    positionStart,
    positionEnd,
    z,
  }: {
    fillStart: RgbaU32;
    fillEnd: RgbaU32;
    lineWidth: number;
    name?: string;
    positionStart: Vector3;
    positionEnd: Vector3;
    z: number;
  }) {
    super(
      new LineGeometry(),
      new LineMaterial({
        color: new Color(0xffffff),
        linewidth: lineWidth,
        vertexColors: true,
      }),
    );
    if (name) {
      this.name = name;
    }
    this.curve = [];
    this._fillStart = fillStart;
    this._fillEnd = fillEnd;
    this.setEndpoints(positionStart, positionEnd);
    this.updateFill(fillStart, fillEnd);
    this.position.z = z;
  }

  updateFill(start: RgbaU32, end: RgbaU32) {
    this._fillStart = start;
    this._fillEnd = end;
    // We need the conversion from SRGBColorSpace to LinearSRGBColorSpace or else it gets bad
    const sc = new Color((start >> 8) & 0xffffff);
    const ec = new Color((end >> 8) & 0xffffff);
    const colors = [];
    // There's some confusing logic where the last color seems to be ignored due a to `- 3` in
    // three. So whatever, just add an extra point and pray.
    for (let i = 0; i <= SPLINE_POINT_COUNT; ++i) {
      const a = i / SPLINE_POINT_COUNT;
      const c = sc.lerp(ec, a);
      colors.push(c.r, c.g, c.b);
    }
    this.geometry.setColors(colors);
  }

  get fillStart(): RgbaU32 {
    return this._fillStart;
  }

  get fillEnd(): RgbaU32 {
    return this._fillEnd;
  }

  get midpoint(): [x: number, y: number] {
    const center = Math.floor(this.curve.length / 4) * 2;
    return this.curve.slice(center, center + 2) as [number, number];
  }

  setEndpoints(positionStart: Vector3, positionEnd: Vector3): Spline {
    this.curve = calculateCurve(positionStart, positionEnd);
    this.geometry.setPositions(this.curve);
    return this;
  }
}

function calculateCurve(output: Vector3, input: Vector3): number[] {
  const b = output.clone().setX(output.x + 50);
  const c = input.clone().setX(input.x - 50);
  return new CubicBezierCurve3(output, b, c, input)
    .getPoints(SPLINE_POINT_COUNT)
    .flatMap(p => [p.x, p.y, p.z]);
}
